import { motion } from "framer-motion";
import { EASE, MaskedLine, FadeUp } from "@/components/motion";
import { ArrowDiag, Sparkle } from "@/components/Icons";

const PROJECTS = [
  {
    name: "Opération Malfaiteur",
    tag: "Site web",
    desc: "Dératisation & désinsectisation en Île-de-France.",
    img: "/assets/projet-01.jpg",
    imgMobile: "/assets/projet-01-mobile.jpg",
    url: "https://operationmalfaiteur.com/",
    testId: "project-card-1",
  },
  {
    name: "Bestconduite78",
    tag: "Site web",
    desc: "Auto-école à Chatou — permis B, code & conduite accompagnée.",
    img: "/assets/projet-02.jpg",
    imgMobile: "/assets/projet-02-mobile.jpg",
    url: "https://bestconduite78.fr/",
    testId: "project-card-2",
  },
  {
    name: "Locakech",
    tag: "Site web",
    desc: "Location de voitures à Marrakech — citadines, premium & sport.",
    img: "/assets/projet-03.jpg",
    imgMobile: "/assets/projet-03-mobile.jpg",
    url: "https://locakech.flytab.fr/",
    testId: "project-card-3",
  },
  { name: "Projet 04", tag: "Site web", testId: "project-card-4" },
];

const Card = ({ project, index }) => {
  const Tag = project.url ? motion.a : motion.article;
  const linkProps = project.url ? { href: project.url, target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <Tag
      {...linkProps}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -8% 0px" }}
      transition={{ duration: 0.85, ease: EASE, delay: (index % 2) * 0.12 }}
      className="group block bg-flytab-cream border-2 border-flytab-black overflow-hidden transition-colors duration-300 hover:bg-flytab-black hover:text-flytab-cream"
      data-testid={project.testId}
    >
      <div className="relative aspect-[16/10] bg-flytab-black flex items-center justify-center overflow-hidden">
        {project.img ? (
          <div className="absolute inset-0" style={{ perspective: "1000px" }}>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] aspect-square rounded-full bg-flytab-green/20 blur-3xl" />
            <div className="absolute left-[4%] top-1/2 w-[74%] transition-transform duration-700 ease-out [transform:translateY(-50%)_rotateY(-14deg)_rotateX(4deg)_rotateZ(1deg)] group-hover:[transform:translateY(-50%)_rotateY(-3deg)_rotateX(0deg)_rotateZ(0deg)]">
              <div className="rounded-t-lg bg-[#161616] border-2 border-b-0 border-[#2A2A2A] px-3 py-1.5 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-flytab-green shrink-0" />
                <span className="w-2 h-2 rounded-full bg-neutral-600 shrink-0" />
                <span className="w-2 h-2 rounded-full bg-neutral-600 shrink-0" />
                <span className="ml-1.5 flex-1 truncate rounded-full bg-black px-3 py-0.5 font-mono text-[9px] md:text-[10px] text-flytab-green text-left">
                  {project.url.replace("https://", "")}
                </span>
              </div>
              <div className="border-2 border-t-0 border-[#2A2A2A] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.65)]">
                <img
                  src={project.img}
                  alt={`Aperçu du site ${project.name}`}
                  loading="lazy"
                  className="w-full aspect-[16/10] object-cover object-top"
                />
              </div>
            </div>
            {project.imgMobile && (
              <div className="absolute right-[4%] bottom-[-3%] w-[21%] transition-transform duration-700 ease-out [transform:rotateY(10deg)_rotateZ(-5deg)] group-hover:[transform:rotateY(2deg)_rotateZ(-1deg)]">
                <div className="relative rounded-[1.4rem] border-[5px] border-[#161616] bg-[#161616] overflow-hidden shadow-[0_20px_45px_rgba(0,0,0,0.65)]">
                  <img
                    src={project.imgMobile}
                    alt={`Aperçu mobile du site ${project.name}`}
                    loading="lazy"
                    className="w-full aspect-[9/19] object-cover object-top"
                  />
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 w-1/3 h-2 bg-[#161616] rounded-full" />
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <Sparkle className="w-12 md:w-16 transition-transform duration-500 group-hover:rotate-45 group-hover:scale-110" stroke="none" strokeWidth={0} />
            <span className="absolute bottom-3 left-4 font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-flytab-green">
              Visuel à venir
            </span>
          </>
        )}
      </div>
      <div className="p-6 md:p-8 flex items-center justify-between gap-4">
        <div>
          <h3 className="font-display font-extrabold uppercase text-xl md:text-2xl tracking-tight">{project.name}</h3>
          <p className="font-mono text-xs md:text-sm opacity-60 mt-1">{project.desc || "Étude de cas en cours de rédaction."}</p>
        </div>
        <span className="shrink-0 rounded-full border-2 border-current px-4 py-1.5 font-mono text-[11px] uppercase tracking-wider">
          {project.tag}
        </span>
      </div>
    </Tag>
  );
};

export default function Realisations() {
  return (
    <section id="realisations" className="on-green bg-flytab-green text-flytab-black px-4 md:px-10 py-24 md:py-32" data-testid="realisations-section">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
        <div>
          <FadeUp>
            <span className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] font-medium">(Réalisations)</span>
          </FadeUp>
          <h2 className="font-display font-black uppercase leading-[0.9] tracking-tight text-5xl md:text-7xl lg:text-8xl mt-4" data-testid="realisations-heading">
            <MaskedLine delay={0.1}>Nos</MaskedLine>
            <MaskedLine delay={0.2}>projets.</MaskedLine>
          </h2>
        </div>
        <FadeUp delay={0.3} className="max-w-xs">
          <p className="font-mono text-sm leading-relaxed opacity-70">
            Un aperçu de nos dernières réussites — les études de cas complètes arrivent très vite.
          </p>
        </FadeUp>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        {PROJECTS.map((p, i) => (
          <Card key={p.testId} project={p} index={i} />
        ))}
      </div>

      <FadeUp delay={0.15} className="mt-12 md:mt-16 flex justify-center">
        <button
          onClick={() => window.__lenis?.scrollTo("#contact", { duration: 1.5 })}
          className="rounded-full bg-flytab-black text-flytab-green font-display font-bold uppercase text-sm md:text-base px-8 py-4 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_8px_0_rgba(0,0,0,0.35)] inline-flex items-center gap-2"
          data-testid="realisations-cta-button"
        >
          Lancer mon projet <ArrowDiag className="w-4 h-4" />
        </button>
      </FadeUp>
    </section>
  );
}
